define([
  'immutable',
  'underscore',
  'es6!sumo/util',
  'es6!launcher/util/listbuilder/wysiwyg/sanitize',
  'es6!launcher/util/listbuilder/validation/popup',
  'es6!launcher/actions/ListBuilderActions',
  'es6!launcher/util/listbuilder/constants',
  'es6!launcher/util/listbuilder/wysiwyg/dom',
  'es6!launcher/util/listbuilder/wysiwyg/util',
  'es6!launcher/util/listbuilder/wysiwyg/constants',
  'es6!launcher/util/listbuilder/wysiwyg/templates',
  'es6!sumo/list/actions/Constants',
], (Immutable, _, Util, WysiwygSanitize, PopupValidator, Actions, Constants, WysiwygDom, WysiwygUtil, WysiwygConstants, WysiwygTemplates, ListConstants) => {
  const { cacheGet, cacheSet, cacheGetSiteNs } = Util;
  const { GridRowLayoutPresets } = WysiwygConstants;

  const getCachedState = () => {
    const cacheKey = `${cacheGetSiteNs()}:forms:state`;
    const cachedState = cacheGet(cacheKey);

    if (cachedState) {
      cachedState.list = Immutable.fromJS(cachedState.list);

      return cachedState;
    }

    return {};
  };

  const cacheState = (state) => {
    const {
      list,
    } = state.toJS();
    const cacheKey = `${cacheGetSiteNs()}:forms:state`;

    // drop down to json so we can serialize
    return cacheSet(cacheKey, {
      list,
    });
  };

  const cachedState = getCachedState();
  const initialState = Immutable.Map({
    list: Immutable.List(),
    testable: Immutable.List(),
    loadedPages: 0,
    // Simplified, unfiltered popup list that contains only campaign_ids and names (used for stats filtering)
    popupIds: Immutable.List(),
    migrateListBuilder: false,
    migrateScrollBox: false,
    migrateSmartBar: false,
    migrateWelcomeMat: false,
    byId: Immutable.Map(),
    hasPopups: false,
    // Save template when selecting a different template.
    savedTemplatesById: Immutable.Map(),
    // Save template id when selecting a different goal.  Use savedTemplatesById
    // to get the json for that template.
    savedTemplateIdsByGoal: Immutable.Map(),
    statusFilter: Immutable.Set(['active', 'paused']),
    textFilter: '',
    errors: Immutable.Map(),
    isPopupChanged: false,
    hasNameChanged: false,
    loading: false,
    undoStackById: Immutable.Map(),
    redoStackById: Immutable.Map(),
    fetchedAt: 0,
    autoResponseDefaultSender: null,
    scrape_error: false,
    ...cachedState,
  });

  // If an attr on the destination object is an object then we call this recursively.
  // It will override arrays.
  // We shouldn't ever need to go more than one layer deep.
  function deepExtend(destination, source) {
    for (let key in source) {
      const value = destination[key];
      if (value) {
        // _.isObject() will return true for arrays and function so need to explicitly check
        // for those.  We only want objects since we can't extend the others.
        if (_.isObject(value) && !_.isArray(value) && !_.isFunction(value)) {
          deepExtend(value, source[key]);
        } else {
          destination[key] = source[key];
        }
      } else {
        destination[key] = source[key];
      }
    }
    // deepExtend modifies in place but for completeness, we'll return as well.
    return destination;
  }

  function processPopupJson(popup) {
    const parsedJsonForPopups = JSON.parse(popup.json || '{}');

    WysiwygSanitize.sanitizePopups(popup.form_type, parsedJsonForPopups);
    // Now that we have sanitized our json, we need to save the sanitized version.
    popup.json = JSON.stringify(parsedJsonForPopups);
    // So we don't have to constantly parse json, we add it as an attr to the popup.
    popup.parsedJson = parsedJsonForPopups;
  }

  function processTabJson(popup) {
    const parsedJsonForPopups = JSON.parse(popup.tab_json || '{}');
    popup.parsedTabJson = parsedJsonForPopups;
  }

  const actionHandlers = {};

  actionHandlers[ListConstants.UPDATE_SERVICE_SETTINGS] = (state, action) => {
    if (action.objectId) {
      const popup = state.get('list').find((popup) => popup.get('campaign_id') === action.objectId);

      if (popup) {
        return state.merge(Immutable.Map({ isPopupChanged: true }));
      }
    }

    return state;
  };

  actionHandlers[Actions.VALIDATE] = (state, action) => {
    const fullState = action.fullState;
    const popupsById = state.get('byId').toJS();
    let hasError = false;

    _.each(popupsById, popup => {
      const initialPopupJson = popup.json;
      popup.initialPopupJson = ` ${initialPopupJson}`.slice(1);

      // This modifies the popup in place so no need to set anything.
      // We only want to validate the popup if the id exists since that means
      // it has loaded.
      if (popup.id) PopupValidator.validate(popup, fullState);

      // Only update parsed json if it changed when validating.
      if (initialPopupJson !== popup.json) processPopupJson(popup);
    });

    const newPopupsState = Immutable.fromJS(popupsById);
    // If popup didn't change, don't update the state.  We can't always update the state
    // because equality checks fail to shortcut rendering.
    if (Immutable.is(state.get('byId'), newPopupsState)) return state;
    return state.setIn(['byId'], newPopupsState);
  }

  actionHandlers[Actions.REQUEST_POPUPS] = (state, action) => state.set('loading', true);

  actionHandlers[Actions.ERROR_POPUP] = (state, action) => {
    const { code, popupId, error } = action;

    let retState = state.mergeIn(['byId', popupId], Immutable.fromJS({ saving: false, loading: false }));

    const errors = {};
    if (_.isArray(error)) {
      error.forEach(err => {
        // Apply each error to the errors object.
        _.extend(errors, err);
      });
    } else {
      _.extend(errors, { [code]: `${error}` });
    }

    return retState.mergeDeepIn(['byId', popupId], Immutable.fromJS({ errors }));
  };

  actionHandlers[Actions.INVALIDATE_POPUPS_CACHE] = (state, action) => {
    return state.set('fetchedAt', 0);
  };

  actionHandlers[Actions.ERROR_POPUPS] = (state, action) => {
    const code = action.code;
    const error = action.error;

    const retState = state.set('loading', false);

    return retState.mergeIn(['errors'], { [code]: `${error}` });
  };

  actionHandlers[Actions.UPDATE_MIGRATE_STATUS] = (state, action) => {
    const migrate = action.migrate;
    const status = action.status;
    return state.set(migrate, status);
  };

  actionHandlers[Actions.RECEIVE_TEST_POPUPS] = (state, action) => {
    const popups = Immutable.fromJS(action.popups);
    const clear = action.clear;

    if (!popups || !popups.size) {
      return state;
    }

    let newState = state;
    if (clear) {
      newState = newState.set('loadedPages', 1);
      return newState.set('testable', popups);
    }

    newState = newState.set('loadedPages', state.get('loadedPages') + 1);
    return newState.set('testable', newState.get('testable').concat(popups));
  };

  actionHandlers[Actions.RECEIVE_POPUPS] = (state, action) => {
    let retState = state.set('loading', false);
    const popups = Immutable.fromJS(action.popups);

    retState = retState.merge(Immutable.Map({
      isPopupChanged: false,
    }));
    retState = retState.set('list', popups.filter((popup) => popup.get('migrated_at') === undefined));

    retState = retState.set('migrateListBuilder', action.migrateListBuilder);
    retState = retState.set('migrateScrollBox', action.migrateScrollBox);
    retState = retState.set('migrateSmartBar', action.migrateSmartBar);
    retState = retState.set('migrateWelcomeMat', action.migrateWelcomeMat);

    retState = retState.deleteIn(['errors', 'fetch']);
    retState = retState.set('fetchedAt', Date.now());

    // store for next load
    cacheState(retState);

    return retState;
  };

  actionHandlers[Actions.RECEIVE_POPUP_IDS] = (state, action) => {
    const popups = Immutable.fromJS(action.popups);
    return state.set('popupIds', popups);
  };

  actionHandlers[Actions.REQUEST_POPUP] = (state, action) => {
    return state.mergeIn(['byId', action.id], Immutable.fromJS({ loading: true }));
  };

  actionHandlers[Actions.RECEIVE_POPUP] = (state, action) => {
    const { popup, resetFetch } = action;
    if (!popup) return state;

    processPopupJson(popup);
    processTabJson(popup);

    let name;
    try {
      name = WysiwygUtil.generatePopupName(JSON.parse(popup.json), new Date(popup.created_at));
    } catch (err) { /* noop */ }

    // Save current json for later.
    let newState = state.setIn(['savedTemplatesById', popup.id, popup.form_type, popup.list_builder_template_id], popup.json);
    newState = newState.merge(Immutable.Map({
      isPopupChanged: false,
      hasNameChanged: ('name' in popup) ? name !== popup.name : false,
      ...resetFetch && { fetchedAt: Date.now() },
    }));
    return newState.setIn(['byId', popup.id], Immutable.fromJS(popup));
  };

  actionHandlers[Actions.DELETE_POPUP] = (state, action) => (
    state.deleteIn(['byId', action.popupId])
  );

  actionHandlers[Actions.UPDATE_POPUP] = (state, action) => {
    const { popup, popupId } = action;
    // Add ids to popup json only if we are updating the popup json.
    if ('json' in popup) processPopupJson(popup);
    if ('tab_json' in popup) processTabJson(popup);

    let retState = state.mergeIn(['byId', popupId], Immutable.fromJS(popup));

    const list = retState.get('list');

    // FIXME: what does this even do? this block doesn't even work as expected and not sure `list` is
    // used anywhere else
    list.forEach((p, index) => {
      if (p.id === popupId) { // should be p.get('id') ?
        const updates = _.extend({}, p, _.pick(popup, Object.keys(p))); // should be p.toJS() ?
        retState = retState.setIn(['list', index], updates);
      }
    });

    return retState.merge({
      isPopupChanged: true,
      hasNameChanged: 'name' in popup || retState.get('hasNameChanged'),
    });
  };

  actionHandlers[Actions.GENERATE_FORM_POPUP] = (state, action) => {
    const FIELD_COMPONENTS = [
      'textField',
      'checkbox',
      'dropdown',
      'radio',
      'checkboxGroup',
      'hidden',
    ];
    // Return if invalid data
    const { popupId, wysiwygPopupMode, templateJSON } = action;
    if (!(popupId && wysiwygPopupMode && templateJSON)) return state;
    const popup = state.get('byId').get(popupId);
    if (!popup) return state;
    // Get the new popup mode and make a copy of the opposite popup
    const newPopupMode = (wysiwygPopupMode === 'success') ? 'subscribe' : 'success';
    const immutablePopupJson = popup.get('parsedJson') || Immutable.Map();
    const popupJson = immutablePopupJson.toJS();
    const cloneJsonPopup = JSON.parse(JSON.stringify(popupJson[wysiwygPopupMode]));
    let newPopupComponents;
    let cloneSubmitButton;

    // Change all id's in cloned json to new id's
    cloneJsonPopup.components.forEach((val) => {
      val.id = Util.generateId64();
    });

    if (popup.get('goal') === 'emails') {
      // Remove all field type components and replace the submit button with close
      if (wysiwygPopupMode === 'subscribe') {
        newPopupComponents = _.reject(cloneJsonPopup.components, (component) => {
          if (component.action === 'submit') {
            cloneSubmitButton = component;
            return true;
          }
          return _.contains(FIELD_COMPONENTS, component.type);
        });
        if (cloneSubmitButton) {
          cloneSubmitButton.text = 'Continue';
          cloneSubmitButton.action = 'close';
          newPopupComponents.push(cloneSubmitButton);
        }
      } else {
        // Replace close button with submit and add email field (wysiwygPopupMode === 'success')
        const closeButtonIndex = _.findIndex(cloneJsonPopup.components, val =>
          val.action === 'close' && val.text);
        // Find/modify close button if present and set email field to those dimensions
        if (closeButtonIndex > -1) {
          const closeButton = cloneJsonPopup.components[closeButtonIndex];
          closeButton.text = 'Subscribe Now';
          closeButton.action = 'submit';
          // Get new email field from template and copy dimensions of button
          const newEmailField = _.find(templateJSON.subscribe.components, value =>
            value.type === 'textField' && value.name === 'email');
          const newEmailFieldHeight = parseInt(newEmailField.style.height.match(/\d+/)[0], 10);
          const closeButtonPosition = parseInt(closeButton.style.top.match(/\d+/)[0], 10);
          newEmailField.style.top = `${closeButtonPosition - newEmailFieldHeight}px`;
          newEmailField.style.left = closeButton.style.left;
          newEmailField.style.height = closeButton.style.height;
          newEmailField.style.width = closeButton.style.width;
          // Add all components together
          newPopupComponents = cloneJsonPopup.components;
          newPopupComponents.push(newEmailField);
        }
      }
    } else if (popup.get('goal') === 'push') {
      // Push notifications change 'close' to 'push' button and vice versa
      const actionType = (wysiwygPopupMode === 'subscribe') ? 'push' : 'close';
      const buttonIndex = _.findIndex(cloneJsonPopup.components, val =>
        val.action === actionType && val.text);

      if (buttonIndex > -1) {
        cloneJsonPopup.components[buttonIndex].text = (actionType === 'push') ? 'Continue' : 'Subscribe Now';
        cloneJsonPopup.components[buttonIndex].action = (actionType === 'push') ? 'close' : 'push';
        cloneJsonPopup.components[buttonIndex].id = Util.generateId64();
      }

      newPopupComponents = cloneJsonPopup.components;
    }

    popupJson[newPopupMode] = deepExtend(popupJson[newPopupMode] || {}, { components: newPopupComponents });
    const newPopupPartial = { json: JSON.stringify(popupJson) };
    processPopupJson(newPopupPartial);

    const retState = state.mergeIn(['byId', popupId], Immutable.fromJS(newPopupPartial));

    return retState.merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.UPDATE_POPUP_JSON] = (state, action) => {
    const { popupId, popupStep, popup: popupPartial } = action;
    if (!popupId || !popupStep || !popupPartial) return state;

    const popup = state.get('byId').get(popupId);
    if (!popup) return state;

    const immutablePopupJson = popup.get('parsedJson') || Immutable.Map();
    const popupJson = immutablePopupJson.toJS();

    popupJson[popupStep] = deepExtend(popupJson[popupStep] || {}, popupPartial);

    const newPopupPartial = { json: JSON.stringify(popupJson) };
    processPopupJson(newPopupPartial);

    let retState = state.mergeIn(['byId', popupId], Immutable.fromJS(newPopupPartial));

    return retState.merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.UPDATE_POPUP_JSON_COMPONENT] = (state, action) => {
    const { popupId, componentId, component: componentPartial, shouldBypassUndo } = action;
    if (!popupId || !componentId || !componentPartial) return state;

    const popup = state.get('byId').get(popupId);
    if (!popup) return state;

    const immutablePopupJson = popup.get('parsedJson') || Immutable.Map();
    const popupJson = immutablePopupJson.toJS();
    let component;
    let gameComponents = [];

    // Go through all of the popup steps to look for the component
    for (const popupStep in popupJson) {
      const popupJsonStep = popupJson[popupStep] || {};
      const components = popupJsonStep.components || [];
      const descendantComponents = WysiwygDom.getDescendantComponentsJSON(components);
      if (!component) component = _.findWhere(descendantComponents, { id: componentId });
      gameComponents = gameComponents.concat(_.where(descendantComponents, { type: 'game' }));
    }

    if (!component) return state;

    // If a component attr is an object, we want to extend it rather than set it.
    // This is primarily for updating style.
    deepExtend(component, componentPartial);

    if (component.type === 'game' && 'options' in componentPartial) {
      // Copy non-style changes to other game components
      gameComponents.forEach(component => deepExtend(component, _.pick(componentPartial, 'options')));
    }

    const popupPartial = { json: JSON.stringify(popupJson) };
    processPopupJson(popupPartial);

    let isPopupChanged = state.get('isPopupChanged');

    // image loads fire a state change that shouldn't be considered a change to the popup
    if (_.keys(componentPartial).length === 1 && 'defaultAspectRatio' in componentPartial) {
      isPopupChanged = isPopupChanged || false;
    } else if (!shouldBypassUndo) {
      isPopupChanged = true;
    }

    const retState = state.mergeIn(['byId', popupId], Immutable.fromJS(popupPartial));
    return retState.merge(Immutable.Map({ isPopupChanged }));
  };

  actionHandlers[Actions.START_UNDO_SESSION] = (state, action) => {
    const { popupId } = action;
    let undoStack = state.getIn(['undoStackById', popupId]);
    let redoStack = state.getIn(['redoStackById', popupId]);
    let retState = state;

    // ensure we have a space to work
    if (!undoStack) {
      undoStack = Immutable.List();
      redoStack = Immutable.List();
      retState = retState.setIn(['undoStackById', popupId], undoStack);
      retState = retState.setIn(['redoStackById', popupId], redoStack);
    }

    // store the rollback indices for undo/redo should changes not be persisted
    retState = retState.set('isUndoSessionAvailable', true);
    retState = retState.set('undoSession', {
      undoRollbackState: undoStack,
      redoRollbackState: redoStack,
    });

    return retState;
  };

  actionHandlers[Actions.END_UNDO_SESSION] = (state, action) => {
    const { popupId, shouldRollbackSession } = action;
    const undoSession = state.get('undoSession');
    let retState = state;

    if (shouldRollbackSession && undoSession) {
      retState = retState.setIn(['undoStackById', popupId], undoSession.undoRollbackState);
      retState = retState.setIn(['redoStackById', popupId], undoSession.redoRollbackState);
    }

    retState = retState.delete('undoSession');

    return retState;
  };

  // if appropriate, registers a new popup undo from the given state
  actionHandlers[Actions.REGISTER_UNDO] = (state, action) => {
    const { popupId, popupState, selectedComponentId } = action;
    const isUndoSessionAvailable = state.get('isUndoSessionAvailable');
    let retState = state;

    // don't allow if no undo session exists
    if (isUndoSessionAvailable) {
      // push the current state of the popup on the undo stack
      const undoStack = retState.getIn(['undoStackById', popupId]);
      let undoPopupState = popupState;

      // filter out non-undoable items
      undoPopupState = undoPopupState.delete('name');
      undoPopupState = undoPopupState.delete('updated_at');

      // build the undo object
      const undo = {
        selectedComponentId,
        popupId,
        popupState: undoPopupState,
      };

      // store the undo in the new state
      retState = retState.setIn(['undoStackById', popupId], undoStack.push(undo));

      // new undos mean no more redos, wipe em out
      retState = retState.setIn(['redoStackById', popupId], Immutable.List());
    }

    return retState;
  };

  actionHandlers[Actions.UNDO_POPUP_CHANGE] = (state, action) => {
    const { popupId } = action;
    let retState = state;

    // get the next undo
    const undo = retState.getIn(['undoStackById', popupId])?.last();

    if (undo) {
      // get or create the redo stack
      const redoStack = retState.getIn(['redoStackById', popupId]) || Immutable.List();

      // push the current state as a redo before we process the undo
      retState = retState.setIn(['redoStackById', popupId], redoStack.push({
        popupId,
        popupState: retState.get('byId').get(popupId),
        selectedComponentId: undo.selectedComponentId,
      }));

      // pop the undo
      retState = retState.setIn(['undoStackById', popupId], retState.getIn(['undoStackById', popupId]).pop());

      // return the popup to the previous state
      retState = retState.mergeIn(['byId', popupId], undo.popupState);
    }

    return retState;
  };

  actionHandlers[Actions.REDO_POPUP_CHANGE] = (state, action) => {
    const { popupId } = action;
    let retState = state;

    // get the next redo
    const redo = retState.getIn(['redoStackById', popupId]).last();

    if (redo) {
      // push the current state as an undo before we process the redo
      retState = retState.setIn(['undoStackById', popupId], retState.getIn(['undoStackById', popupId]).push({
        popupState: retState.get('byId').get(popupId),
        selectedComponentId: action.selectedComponentId,
        popupId: redo.popupId,
      }));

      // pop the redo
      retState = retState.setIn(['redoStackById', popupId], retState.getIn(['redoStackById', popupId]).pop());

      // return the popup to the previous state
      retState = retState.mergeIn(['byId', popupId], redo.popupState);
    }

    return retState;
  };

  actionHandlers[Actions.SAVE_POPUP] = (state, action) => state.mergeIn(['byId', action.popupId], Immutable.fromJS({ saving: true }));

  actionHandlers[Actions.RECEIVE_CLICK_TRIGGER] = (state, action) => {
    const clickTrigger = action.clickTrigger;
    const campaignId = clickTrigger.campaign_id;
    const popupsById = state.get('byId').toJS();

    _.each(popupsById, (popup) => {
      if (popup.campaign_id === campaignId) popup.click_triggers.push(clickTrigger);
    });

    return state.set('byId', Immutable.fromJS(popupsById));
  };

  actionHandlers[Actions.UPDATE_CLICK_TRIGGER] = (state, action) => {
    const clickTriggerId = action.id;
    const popupId = action.popupId;
    const clickTrigger = action.clickTrigger;
    const popupsById = state.get('byId').toJS();

    let popup = _.findWhere(popupsById, { id: popupId });

    if (popup) {
      popup.click_triggers = popup.click_triggers || [];
      const clickTriggerToUpdate = _.findWhere(popup.click_triggers, { id: clickTriggerId });

      if (!clickTriggerToUpdate) {
        popup.click_triggers.push(clickTrigger);
      } else {
        _.extend(clickTriggerToUpdate, clickTrigger);
      }
    }

    let retState = state.set('byId', Immutable.fromJS(popupsById));
    return retState.merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.DELETE_CLICK_TRIGGER] = (state, action) => {
    const clickTriggerId = action.id;
    const popupsById = state.get('byId').toJS();

    _.every(_.values(popupsById), (popup) => {
      const clickTriggerToDelete = _.findWhere(popup.click_triggers, {
        id: clickTriggerId,
      });

      if (clickTriggerToDelete) {
        _.extend(clickTriggerToDelete, { deleted: true });
        return false;
      }

      return true;
    });

    let retState = state.set('byId', Immutable.fromJS(popupsById));
    return retState.merge(Immutable.Map({ isPopupChanged: true }));
  };

  // merges components from old popup into new popup from template
  actionHandlers[Actions.UPDATE_POPUP_TEMPLATE] = (state, action) => {
    const { popupId, template } = action;
    const popup = state.get('byId').get(popupId);
    const options = action.options || { persistHiddenComponents: true };
    if (!template || !popup) return state;

    // create an empty clone of the popup
    const immutablePopupJson = popup.get('parsedJson') || Immutable.Map();
    const popupJson = immutablePopupJson.toJS();
    const popupStep = 'subscribe';
    const persistentJson = {
      components: [],
    };

    // if permitted, copy hidden components to the new popup
    if (popupJson && popupStep in popupJson && popupJson[popupStep] && options.persistHiddenComponents !== false) {
      popupJson[popupStep].components.forEach((component) => {
        if (component.type === 'hidden') {
          persistentJson.components.push(component);
        }
      });
    }

    // merge new template data and components from old popup into new popup clone
    const templateId = template.get('id');
    const templateJson = template.get('json');
    if (!templateJson || !templateId) return state;
    let parsedTemplateJson = JSON.parse(templateJson);

    if (parsedTemplateJson && popupStep in parsedTemplateJson && parsedTemplateJson[popupStep] && persistentJson.components.length) {
      persistentJson.components.forEach((component) => {
        parsedTemplateJson[popupStep].components.push(component);
      });
    }

    // Update template's social buttons with selected social goal
    if (parsedTemplateJson && popupStep in parsedTemplateJson && parsedTemplateJson[popupStep] && options.socialChannel) {
      parsedTemplateJson[popupStep].components.forEach((component) => {
        if (component.type === 'socialButton') {
          component.socialChannel = options.socialChannel;
        }
      });
    }

    const savedTemplatesForPopup = state.get('savedTemplatesById').get(popupId);
    const savedTemplatesForFormType = savedTemplatesForPopup ? savedTemplatesForPopup.get(template.get('form_type')) : Immutable.Map();
    const savedTemplateJson = savedTemplatesForFormType ? savedTemplatesForFormType.get(templateId) : null;
    let parsedSavedTemplateJson;
    if (savedTemplateJson) {
      parsedSavedTemplateJson = JSON.parse(savedTemplateJson);
      if (parsedSavedTemplateJson && popupStep in parsedSavedTemplateJson && parsedSavedTemplateJson[popupStep] && persistentJson.components.length) {
        // Remove existing hidden fields
        parsedSavedTemplateJson[popupStep].components = _.reject(parsedSavedTemplateJson[popupStep].components, (component) => {
          return component.type === 'hidden';
        });

        // Add current hidden fields
        persistentJson.components.forEach((component) => {
          parsedSavedTemplateJson[popupStep].components.push(component);
        });
      }

      // Update cached/saved template's social buttons with selected social goal
      if (parsedSavedTemplateJson && popupStep in parsedSavedTemplateJson && parsedSavedTemplateJson[popupStep] && options.socialChannel) {
        parsedSavedTemplateJson[popupStep].components.forEach((component) => {
          if (component.type === 'socialButton') {
            component.socialChannel = options.socialChannel;
          }
        });
      }
    }

    let newState = state;
    let popupPartial = { list_builder_template_id: templateId };

    const oldTemplateJson = popup.get('json');
    const oldTemplateId = popup.get('list_builder_template_id');

    // Save current json for later.
    newState = newState.setIn(['savedTemplatesById', popupId, popup.get('form_type'), oldTemplateId], oldTemplateJson);

    if (savedTemplateJson) {
      // Restore old json
      popupPartial.json = JSON.stringify(parsedSavedTemplateJson);
    } else {
      // Set to new json.
      popupPartial.json = JSON.stringify(parsedTemplateJson);
    }

    // We can give it a partial since all it wants is the json attr.
    processPopupJson(popupPartial);

    return newState.mergeIn(['byId', popupId], Immutable.fromJS(popupPartial));
  };

  // When updating popup goal, we first check for saved templates (in reducer), then we look
  // for a template in the current template group with the goal, and then fall
  // back to the template for the goal in the default template group.
  actionHandlers[Actions.UPDATE_POPUP_GOAL] = (state, action) => {
    const {
      popupId,
      goal,
      currentTemplateForGoal,
      defaultTemplateForGoal,
      modTemplateJSON,
      scrapePopupData,
      templateStatus,
    } = action;
    const popup = state.get('byId').get(popupId);
    const isCustom = goal === 'custom';
    const isPrivateTemplate = templateStatus === 'private';

    // We only need to make sure we have a default template to fall back on.
    if (!isCustom && (!defaultTemplateForGoal || !popup)) return state;

    // Don't update if goal is already selected.
    if (popup.get('goal') === goal && !scrapePopupData) return state;

    const oldGoal = popup.get('goal');
    const oldTemplateId = popup.get('list_builder_template_id');
    const savedTemplateIdsForPopup = state.get('savedTemplateIdsByGoal').get(popupId) || Immutable.Map();
    const savedTemplateId = savedTemplateIdsForPopup.get(goal);
    let newTemplate;

    if (modTemplateJSON) {
      const validTemplateForGoal = currentTemplateForGoal || defaultTemplateForGoal;
      newTemplate = {
        id: validTemplateForGoal.get('id'),
        json: modTemplateJSON,
      };
    } else if (currentTemplateForGoal) {
      // If we have a template for the goal in the current template group, then we want to update
      // to that template.
      newTemplate = {
        id: currentTemplateForGoal.get('id'),
        json: currentTemplateForGoal.get('json'),
      };
    } else if (!currentTemplateForGoal && isPrivateTemplate) {
      // If we don't have a template for the goal in the current template group,
      // means we need to fall back to the default template group.
      // this start happens after My-Template feature.
      newTemplate = {
        id: defaultTemplateForGoal.get('id'),
        json: defaultTemplateForGoal.get('json'),
      };
    } else if (savedTemplateId && goal !== 'push' && popup.get('goal') !== 'push') {
      // If we have a saved template id, that means we also have json saved for that template.
      // All goal updates go through the UPDATE_POPUP_TEMPLATE action which saves the previous
      // json.  All we have to do is pass the id and make sure the json attr isn't null.
      // Push notifications will have an id but no saved json except default template.
      newTemplate = { id: savedTemplateId, json: '{}' };
    } else if (!isCustom) {
      // Fall back to the template for the goal in the default template group.
      // The check above for defaultTemplateForGoal should ensure this always has something.
      newTemplate = {
        id: defaultTemplateForGoal.get('id'),
        json: defaultTemplateForGoal.get('json'),
      };
    }

    let newState = state;
    newState = newState.setIn(['savedTemplateIdsByGoal', popupId, oldGoal], oldTemplateId);
    newState = newState.mergeIn(['byId', popupId], { goal });

    return actionHandlers[Actions.UPDATE_POPUP_TEMPLATE](
      newState,
      { template: Immutable.Map(newTemplate), popupId }
    );
  };

  // If changing the social shares channel, just change the current
  // socialButton component's socialChannel attribute instead of
  // changing the entire template.
  actionHandlers[Actions.UPDATE_POPUP_SOCIAL_CHANNEL] = (state, action) => {
    const {
      popupId,
      socialChannel,
      currentTemplate,
    } = action;
    const popup = state.get('byId').get(popupId);
    let hasSocialButton;

    if (!popup) return state;

    const immutablePopupJson = popup.get('parsedJson') || Immutable.Map();
    const popupJson = immutablePopupJson.toJS();
    const subscribeJson = popupJson['subscribe'] || {};
    const components = subscribeJson.components || [];
    components.forEach((component) => {
      if (component.type === 'socialButton') {
        component.socialChannel = socialChannel;
        hasSocialButton = true;
      }
    });

    // If there's no social button and user selects a social goal, add the template default
    if (!hasSocialButton) {
       const templateJson = JSON.parse(currentTemplate.get('json'));
       const templateSubscribeJson = templateJson['subscribe'] || {};
       const templateComponents = templateSubscribeJson.components || [];
       templateComponents.forEach((component) => {
         if (component.type === 'socialButton') {
           component.socialChannel = socialChannel;
           components.push(component);
         }
       });
    }

    const popupPartial = { json: JSON.stringify(popupJson) };
    processPopupJson(popupPartial);

    return state.mergeIn(['byId', popupId], Immutable.fromJS(popupPartial));
  };

  actionHandlers[Actions.UPDATE_POPUP_TEXT_FILTER] = (state, action) => {
    return state.set('textFilter', action.filter);
  };

  actionHandlers[Actions.UPDATE_SCRAPE_STATUS] = (state, action) => {
    return state.set('scrape_error', action.status);
  };

  actionHandlers[Actions.UPDATE_POPUP_STATUS_FILTER] = (state, action) => {
    let newFilter = new Immutable.Set(action.filter);
    let filterArr = newFilter.toArray();

    if (filterArr.length <= 1) {
      newFilter = new Immutable.Set(['active']).concat(newFilter);
    }

    return state.set('statusFilter', newFilter);
  };

  actionHandlers[Actions.UPDATE_POPUP_FORM_TYPE] = (state, action) => {
    const { popupId, formType } = action;
    const templates = action.templates ? action.templates.toJS() : [];
    const popupsById = state.get('byId').toJS();

    let popup = _.findWhere(popupsById, { id: popupId });

    let newState = state;

    let newTemplateFormType;
    let oldTemplateFormType;

    switch(formType) {
      // Form types that share templates
      case Constants.FormTypes.POPUP:
      case Constants.FormTypes.SCROLL_BOX:
      case Constants.FormTypes.INLINE_POPUP:
      case Constants.FormTypes.CLICK_TRIGGER_POPUP:
        newTemplateFormType = Constants.FormTypes.POPUP;
        break;
      default:
        newTemplateFormType = formType;
        break;
    }

    switch(popup.form_type) {
      // Form types that share templates
      case Constants.FormTypes.POPUP:
      case Constants.FormTypes.SCROLL_BOX:
      case Constants.FormTypes.INLINE_POPUP:
      case Constants.FormTypes.CLICK_TRIGGER_POPUP:
        oldTemplateFormType = Constants.FormTypes.POPUP;
        break;
      default:
        oldTemplateFormType = popup.form_type;
        break;
    }

    if (oldTemplateFormType !== newTemplateFormType) {
      // Switching between form types with different templates.
      const newTemplate = _.findWhere(templates, { form_type: newTemplateFormType, slug: 'default' });
      if (newTemplate) {
        newState = actionHandlers[Actions.UPDATE_POPUP_TEMPLATE](
          newState,
          { template: Immutable.Map(newTemplate), popupId }
        );
      }

      // Switch goal to emails if switching to welcome mat
      if (newTemplateFormType === Constants.FormTypes.WELCOME_MAT) {
        newState = newState.mergeIn(['byId', popupId], { goal: 'emails' });
      }

      const isDefaultTimer = (
        popup.visibility_mode === 'custom'
        && popup.popup_mode === 'manual'
        && popup.before_show_delay === '5'
      );

      // if new form type is smart bar or welcome mat check smart visibility options
      if (popup.visibility_mode === 'default' || isDefaultTimer) {
        const popupPartial = { cookie_lifetime_interval: 'month' };
        if (newTemplateFormType === Constants.FormTypes.SMART_BAR || newTemplateFormType === Constants.FormTypes.WELCOME_MAT) {
          popupPartial.cookie_lifetime_interval = 'always';
        }

        newState = actionHandlers[Actions.UPDATE_POPUP](
          newState,
          { popupId, popup: popupPartial }
        );
      }
    }

    newState = newState.setIn(['byId', popupId, 'form_type'], formType);

    return newState;
  };

  actionHandlers[Actions.ADD_AUTO_RESPONSE_RULE] = (state, action) => {
    const variantId = action.variantId;
    const autoResponseRule = action.autoResponseRule;
    const popupsById = state.get('byId').toJS();

    // TODO: Revisit if more than one email is ever supported
    const popup = _.findWhere(popupsById, { id: variantId });
    if (popup) {
      autoResponseRule.status = 'inactive';
      popup.auto_responses = popup.auto_responses || [];
      popup.auto_responses = [autoResponseRule];
    }

    return state
      .set('byId', Immutable.fromJS(popupsById))
      .merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.REMOVE_AUTO_RESPONSE_RULE] = (state, action) => {
    const variantId = action.variantId;
    const autoResponseRuleId = action.autoResponseRuleId;
    const popupsById = state.get('byId').toJS();

    const popup = _.findWhere(popupsById, { id: variantId });

    if (popup) {
      popup.auto_responses = popup.auto_responses || [];
      popup.auto_responses = popup.auto_responses.filter(r => r.id !== autoResponseRuleId);
    }

    return state
      .set('byId', Immutable.fromJS(popupsById))
      .merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.TOGGLE_AUTO_RESPONSE_RULE_STATUS] = (state, action) => {
    const {
      variantId,
      autoResponseRuleId,
    } = action;

    const popupsById = state.get('byId').toJS();

    const popup = _.findWhere(popupsById, { id: variantId });

    if (popup) {
      popup.auto_responses = popup.auto_responses || [];
      const autoResponseRuleToUpdate = _.findWhere(popup.auto_responses, {
        id: autoResponseRuleId,
      });

      if (autoResponseRuleToUpdate) {
        let { status } = autoResponseRuleToUpdate;
        if (status === 'active' || status === 'inactive') {
          status = 'paused';
        } else {
          status = 'inactive';
        }
        deepExtend(autoResponseRuleToUpdate, { status });
      }
    }

    const retState = state.set('byId', Immutable.fromJS(popupsById));
    return retState.merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.SET_AUTO_RESPONSE_RULES_STATUS_FROM_LIST] = (state, action) => {
    const onlyOneOn = action.onlyOneOn;
    const objectId = action.objectId;
    const variantId = action.variantId;
    const status = action.status;
    const popupsList = state.get('list').toJS();

    let popup = _.findWhere(popupsList, { id: variantId });

    if (popup && popup.auto_responses && popup.auto_responses.length) {
      popup.auto_responses.forEach(function(rule){
        rule.status = status;
      });
    }

    if (onlyOneOn) {
      let popups = _.reject(popupsList, { id: variantId });
      popups.forEach(function(popup){
        if (popup && popup.auto_responses && popup.auto_responses.length) {
          popup.auto_responses.forEach(function(rule){
            if (rule.status === 'active' || rule.status === 'inactive') rule.status = 'paused';
          });
        }
      });
    }

    let retState = state.set('list', Immutable.fromJS(popupsList));
    return retState.merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.UPDATE_AUTO_RESPONSE_RULE_NAME] = (state, action) => {
    const objectId = action.objectId;
    const variantId = action.variantId;
    const autoResponseEmail = action.autoResponseEmail;
    const popupsById = state.get('byId').toJS();

    let popup = _.findWhere(popupsById, { id: variantId });

    if (popup) {
      popup.auto_responses = popup.auto_responses || [];
      const autoResponseRuleToUpdate = _.findWhere(popup.auto_responses, { email_id: autoResponseEmail.id });

      if (autoResponseRuleToUpdate) {
        deepExtend(autoResponseRuleToUpdate, {email: {name: autoResponseEmail.name, subject: autoResponseEmail.subject}});
      }
    }

    let retState = state.set('byId', Immutable.fromJS(popupsById));
    return retState.merge(Immutable.Map({ isPopupChanged: true }));
  };

  actionHandlers[Actions.ERROR_AUTO_RESPONSE] = (state, action) => {
    const code = action.code;
    const error = action.error;

    const retState = state.set('loading', false);

    return retState.mergeIn(['errors'], { ['auto_response']: `${error}` });
  };

  actionHandlers[Actions.ACK_ERROR_AUTO_RESPONSE] = (state, action) => {
    let retState = state.set('loading', false);

    retState = retState.deleteIn(['errors', 'auto_response']);

    return retState;
  };

  actionHandlers[Actions.UPDATE_POPUP_ROW_LAYOUT] = (state, action) => {
    const { popupId, popupStep, layoutPreset } = action;
    const keyPathForComponents = ['byId', popupId, 'parsedJson', popupStep, 'components'];
    const components = state.getIn(keyPathForComponents);
    const descendantComponents = WysiwygDom.getDescendantComponents(components);
    const rowComponent = descendantComponents.find(component => (component.get('type') === 'gridRow'));
    const keyPathToRowComponent = WysiwygDom.getKeyPathForComponent(components, rowComponent);
    const columns = rowComponent.get('childComponents');
    const currentNumberOfColumns = columns.count();
    const newNumberOfColumns = (layoutPreset === GridRowLayoutPresets.TWO_COLUMN ? 2 : 1)
    const changeInNumberOfColumns = (newNumberOfColumns - currentNumberOfColumns);
    let newColumns = columns;

    // handle collapsing / splitting columns
    if (changeInNumberOfColumns < 0) { // collapsing
      // TODO: better logic for collapsing columns
      newColumns = newColumns.slice(0, newNumberOfColumns);
    } else { // splitting
      for (let i = changeInNumberOfColumns; i > 0; i--) {
        const gridColumnTemplate = WysiwygTemplates.getTemplate(
          'gridColumn',
          Constants.FormTypes.WELCOME_MAT
        );

        newColumns = newColumns.push(Immutable.fromJS(gridColumnTemplate));
      }
    }

    // generate new column layouts
    let rowLayout;

    // TODO: in the future these should be presets, and could include more than just 100% or 50%
    if (newNumberOfColumns === 2) {
      rowLayout = [{ small: 12, medium: 6 }, { small: 12, medium: 6 }];
    } else {
      rowLayout = [{ small: 12 }];
    }

    // map the column layouts to their columns
    newColumns = newColumns.map((column, columnIndex) => {
      let newColumn = column;
      let contentAlign = 'center';

      newColumn = newColumn.set('layouts', Immutable.fromJS(rowLayout[columnIndex]));

      if (layoutPreset === GridRowLayoutPresets.ONE_COLUMN_LEFT) {
        contentAlign = 'left';
      } else if (layoutPreset === GridRowLayoutPresets.ONE_COLUMN_RIGHT) {
        contentAlign = 'right';
      }

      newColumn = newColumn.set('contentAlign', contentAlign);

      return newColumn;
    });

    // fold it all in
    // onChange(selectedRow.set('childComponents', columns));
    const finalKeyPath = keyPathForComponents.concat(keyPathToRowComponent, ['childComponents']);

    return state.setIn(finalKeyPath, newColumns);
  };

  return Util.createReducer(initialState, actionHandlers);
});
